import { useMemoizedFn, useMount } from 'ahooks';
import { Form, Input } from 'antd';
import { useForm } from 'antd/lib/form/Form';
import { useAppDispatch } from 'app/hooks';
import { DeleteIcon } from 'components/Icons';
import { SectionHeading, UnderlineText } from 'components/RevxTypography';
import { StepActionTypeEnum } from 'domains/skuDomain';
import { FieldError } from 'rc-field-form/lib/interface';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StepComponentProps } from '.';
import './Step2Size.less';

export const Step9Extra = ({ sku, topSku, skuPart }: StepComponentProps) => {
  const { t } = useTranslation();
  const [form] = useForm();
  const [errorTip, setErrorTip] = useState<FieldError[]>();

  const dispatch = useAppDispatch();

  useMount(() => {
    dispatch.sku.setRevxSpinloading(true);
    try {
      dispatch.sku.registerStepAction({
        [StepActionTypeEnum.STEP9_EXTRA]: submit,
      });

      if (sku?.extraSpecs?.length) {
        const defaultForm = sku?.extraSpecs.map((item) => {
          return {
            key: Object.keys(item)[0],
            value: Object.values(item)[0],
          };
        });
        form.setFieldsValue({
          list: defaultForm,
        });
      } else {
        form.setFieldsValue({ list: [{}] });
      }

      dispatch.sku.setRevxSpinloading(false);
    } catch {
      dispatch.sku.setRevxSpinloading(false);
    }
  });

  const addField = () => {
    const val = form.getFieldsValue();
    val.list.push({});
    form.setFieldsValue(val);
  };

  const submit = useMemoizedFn(async () => {
    if (sku.readonly) {
      return true;
    }
    try {
      const formValue = await form.validateFields();
      const payload: any[] = [];

      formValue.list.forEach((item: { key: string; value: string }) => {
        const specsObj: { [str: string]: string } = {};
        if (item.key && item.value) {
          specsObj[item.key] = item.value;
          payload.push(specsObj);
        }
      });

      const res = await dispatch.sku.updateExtraSpecs({
        extraSpecs: payload.length ? payload : [],
        id: sku.id,
        version: sku.version,
      });
      return res;
    } catch {
      setErrorTip(form.getFieldsError());
      // do nothing when form invalid
      return false;
    }
  });

  const formChange = async (changedFields: any, allFields: any) => {
    try {
      await form.validateFields();
      setErrorTip([]);
    } catch {
      setErrorTip(form.getFieldsError());
    }
  };

  return (
    <>
      <div className="single-sku-config-wizard-step-title">
        {t('platform.sku.wizard.extraSpecValuesOptional')}
      </div>
      <div className="single-sku-config-wizard-step-content">
        <SectionHeading>
          {t('platform.sku.wizard.extraSpec.title')}.
        </SectionHeading>

        <Form
          form={form}
          className="single-sku-config-sizes"
          onValuesChange={formChange}
        >
          {/* <Form.Item
            label={t(
              'platform.sku.wizard.quantity.form.label.extraSpecificationsValuesSkip'
            )}
            name="skipArtFile">
            <Radio.Group disabled={sku.readonly || Boolean(topSku)}>
              <Radio value={true}>
                <span>Yes</span>
              </Radio>
              <Radio value={false}>
                <span>No</span>
              </Radio>
            </Radio.Group>
          </Form.Item> */}
          <table className="size-table">
            <thead>
              <tr>
                <th>{t('platform.sku.wizard.extraSpec.fieldName')}</th>
                <th>{t('platform.sku.wizard.extraSpec.values')}</th>
              </tr>
            </thead>
            <tbody>
              <Form.List
                name={'list'}
                rules={[
                  {
                    validator: async (_, list) => {
                      const filterList = list.filter(
                        (item: { key: string; value: string }) =>
                          (!item.key && item.value) || (item.key && !item.value)
                      );
                      if (filterList.length) {
                        return Promise.reject(
                          new Error('Field name and values must be paired')
                        );
                      } else {
                        return Promise.resolve();
                      }
                    },
                  },
                ]}
              >
                {(fields, { add, remove }, { errors }) =>
                  fields.map((field) => (
                    <tr key={field.fieldKey}>
                      <td>
                        <Form.Item
                          {...field}
                          label={''}
                          name={[field.name, 'key']}
                          fieldKey={[field.fieldKey, 'key']}
                        >
                          <Input disabled={sku.readonly} />
                        </Form.Item>
                      </td>
                      <td>
                        <Form.Item
                          {...field}
                          label={''}
                          name={[field.name, 'value']}
                          fieldKey={[field.fieldKey, 'value']}
                        >
                          <Input disabled={sku.readonly} />
                        </Form.Item>
                      </td>
                      {fields.length > 1 ? (
                        <div
                          hidden={sku.readonly}
                          className="dynamic-delete-button"
                          onClick={() => remove(field.name)}
                        >
                          <DeleteIcon />
                        </div>
                      ) : null}
                    </tr>
                  ))
                }
              </Form.List>
            </tbody>
          </table>
          <span className="ant-form-item-explain-error">
            {errorTip?.filter((item) => item.errors.length)[0]?.errors[0]}
          </span>
        </Form>

        {!sku.readonly && (
          <UnderlineText
            color="blue"
            size="small"
            style={{ width: 90 }}
            ellipsis
            onClick={() => addField()}
          >
            + {t('platform.sku.wizard.extraSpec.addField')}
          </UnderlineText>
        )}
      </div>
    </>
  );
};
